import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * The single tap-through to the Kin Pro page (issue #50).
 *
 * Every Pro badge/lock in the app routes HERE and nowhere else, so there is
 * exactly one surface that explains what Pro is and what it costs. The rule is
 * still "route to /pro, let that page decide" — but as of #625 what it decides
 * between is already-Pro vs. live pricing. The pre-launch waitlist state and
 * src/lib/waitlist.ts are DELETED: Kin Pro launched Aug 5 2026 and the flag
 * does not flip back.
 *
 * So do not reintroduce a Flodesk link, here or anywhere. If a future launch
 * ever needs a "not on sale yet" state, that is a new design with its own copy,
 * not a resurrection of this one.
 *
 * app_settings.pro_launch_live still EXISTS and is still read — but only by the
 * Siri lock sheet copy (#599, TasksTab.tsx → siriLockCopy.ts). It no longer has
 * anything to do with this page.
 *
 * Presentation + navigation ONLY. Nothing in here reads or changes an
 * entitlement — gating stays in useIsPro / useFeature, untouched.
 * The established pattern this generalizes is the Pro-locked delivery pills in
 * HowKinReachesYou.tsx (previously KinTextsSection.tsx:64, retired Aug 2 2026).
 */

const PERIWINKLE = "#5050C8";

/**
 * Navigate-to-/pro callback, for gate handlers that aren't rendering a link.
 * useCallback-wrapped so callers can safely list it in a useCallback/useMemo
 * dep array — FamilyProfileSection's requirePro does exactly that, and an
 * unstable identity there would silently defeat its memoization.
 */
export const useOpenPro = () => {
  const navigate = useNavigate();
  return useCallback(() => navigate("/pro"), [navigate]);
};

/**
 * Full-width button for the bottom of a Pro lock sheet.
 * Deliberately says "see", not "upgrade" — pre-launch there is nothing to buy
 * yet, and the page is honest about that on its own.
 */
export const ProUpsellButton = ({
  label = "See what's in Kin Pro 💜",
  onNavigate,
}: {
  label?: string;
  /** Called just before navigating — use it to close the host sheet/modal. */
  onNavigate?: () => void;
}) => {
  const openPro = useOpenPro();
  return (
    <button
      type="button"
      className="font-body w-full"
      onClick={() => {
        onNavigate?.();
        openPro();
      }}
      style={{
        backgroundColor: PERIWINKLE,
        color: "#FFFFFF",
        fontSize: 15,
        fontWeight: 600,
        padding: "14px",
        borderRadius: 12,
        border: "none",
        marginTop: 18,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
};

/**
 * Inline periwinkle text link, for the small free-tier cap lines that used to
 * be dead-end copy ("Kin Pro launching soon 💜"). Renders as a button so it's
 * keyboard-reachable, but sits inline in a sentence.
 */
export const ProUpsellLink = ({
  label = "See what's in Kin Pro 💜",
  fontSize = 12,
  onNavigate,
}: {
  label?: string;
  fontSize?: number;
  onNavigate?: () => void;
}) => {
  const openPro = useOpenPro();
  return (
    <button
      type="button"
      className="font-body underline"
      onClick={(e) => {
        // Several hosts (Trip Planner tile, locked cards) put this inside a
        // parent that also has an onClick — don't fire both.
        e.stopPropagation();
        onNavigate?.();
        openPro();
      }}
      style={{
        fontSize,
        fontWeight: 600,
        color: PERIWINKLE,
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
};

/**
 * Toast with a tap-through action, for gate hits that surface as a toast
 * rather than a sheet (Family Profile edit, schedule import cap). Sonner's
 * action button is the only tappable affordance a toast has.
 */
export const useProUpsellToast = () => {
  const openPro = useOpenPro();
  return useCallback(
    (message: string, description?: string) =>
      toast(message, {
        description,
        action: { label: "See Pro", onClick: openPro },
      }),
    [openPro]
  );
};
